import api from '../lib/api';

export interface BlackoutDate {
  id?: number;
  hotel_id?: number;
  room_plan_id?: number;
  blackout_from: string;
  blackout_to: string;
  reason?: string;
  special_rate?: number | null;
  is_hotel_unavailable?: boolean;
  currency?: string;
  description?: string;
  is_non_refundable?: boolean;
  special_cancellation_policy?: string;
  min_stay_override?: number;
  max_stay_override?: number;
  advance_booking_required?: number;
  inventory_multiplier?: number;
  status?: string;
  priority?: number;
  created_at?: string;
  updated_at?: string;
}

export interface BlackoutDateResponse {
  success: boolean;
  data: BlackoutDate;
  message?: string;
}

export interface BlackoutDatesListResponse {
  success: boolean;
  data: BlackoutDate[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
  message?: string;
}

class BlackoutDateService {
  // Get all blackout dates for a hotel
  async getBlackoutDates(hotelId: number, page: number = 1, limit: number = 10): Promise<BlackoutDatesListResponse> {
    const response = await api.get(`/blackout-dates/hotel/${hotelId}?page=${page}&limit=${limit}`);
    return response.data;
  }

  // Get blackout date by ID
  async getBlackoutDateById(id: number): Promise<BlackoutDateResponse> {
    const response = await api.get(`/blackout-dates/${id}`);
    return response.data;
  }

  // Create new blackout date
  async createBlackoutDate(blackoutDateData: Omit<BlackoutDate, 'id' | 'created_at' | 'updated_at'>): Promise<BlackoutDateResponse> {
    const response = await api.post('/blackout-dates', blackoutDateData);
    return response.data;
  }

  // Update blackout date
  async updateBlackoutDate(id: number, blackoutDateData: Partial<BlackoutDate>): Promise<BlackoutDateResponse> {
    const response = await api.put(`/blackout-dates/${id}`, blackoutDateData);
    return response.data;
  }

  // Delete blackout date
  async deleteBlackoutDate(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/blackout-dates/${id}`);
    return response.data;
  }

  // Check if hotel is available for date range
  async checkHotelAvailability(hotelId: number, checkIn: string, checkOut: string): Promise<{ available: boolean; conflicting_dates: string[] }> {
    const response = await api.get(`/blackout-dates/check-availability/${hotelId}?check_in=${checkIn}&check_out=${checkOut}`);
    return response.data;
  }
}

const blackoutDateService = new BlackoutDateService();
export default blackoutDateService;
